import { create } from "zustand";
import {
  User,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, googleProvider } from "../firebase";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5188/api/v1";

export interface SavedLocationDto {
  id: number;
  label: string;
  latitude: number;
  longitude: number;
  savedAt: string;
}

interface AuthState {
  user: User | null;
  idToken: string | null;
  isAuthLoading: boolean;
  savedLocations: SavedLocationDto[];
  isSavedLoading: boolean;

  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  fetchSavedLocations: () => Promise<void>;
  saveLocation: (label: string, lat: number, lng: number) => Promise<void>;
  deleteLocation: (id: number) => Promise<void>;
  initAuth: () => () => void; // returns the unsubscribe function
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  idToken: null,
  isAuthLoading: true,
  savedLocations: [],
  isSavedLoading: false,

  // Subscribe to firebase auth state changes on app boot
  initAuth: () => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        set({ user, idToken: token, isAuthLoading: false });
      } else {
        set({ user: null, idToken: null, isAuthLoading: false, savedLocations: [] });
      }
    });
    return unsubscribe;
  },

  loginWithGoogle: async () => {
    set({ isAuthLoading: true });
    try {
      await signInWithPopup(auth, googleProvider);
    } finally {
      set({ isAuthLoading: false });
    }
  },

  logout: async () => {
    await signOut(auth);
    set({ user: null, idToken: null, savedLocations: [] });
  },

  fetchSavedLocations: async () => {
    const { idToken } = get();
    if (!idToken) return;
    set({ isSavedLoading: true });
    try {
      const res = await fetch(`${API_BASE}/locations`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data: SavedLocationDto[] = await res.json();
        set({ savedLocations: data });
      }
    } finally {
      set({ isSavedLoading: false });
    }
  },

  saveLocation: async (label, lat, lng) => {
    const { idToken } = get();
    if (!idToken) return;
    await fetch(`${API_BASE}/locations`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ label, latitude: lat, longitude: lng }),
    });
    get().fetchSavedLocations();
  },

  deleteLocation: async (id) => {
    const { idToken } = get();
    if (!idToken) return;
    await fetch(`${API_BASE}/locations/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${idToken}` },
    });
    set((s) => ({ savedLocations: s.savedLocations.filter((l) => l.id !== id) }));
  },
}));
